import React from "react";
import InfiniteScroll from "react-infinite-scroller";

import { Box, Button, Card, CardContent, Grid, Typography } from "@material-ui/core";
import { useTheme, withStyles } from "@material-ui/core/styles";

import Spacer from "components/Spacer";

import DashAIComparatorsButton from "./DashAIComparatorsButton";
import Filter from "./Filter";
import Search from "./Search";
import Sort from "./Sort";
import StoreCard from "./StoreCard";

import {
    addStoreToCandidateComparator,
    selectCandidateComparator,
    selectCustomSelectionStores,
    removeStoreFromCandidateComparator
} from "modules/customer/insights/portfolioNew/filters/filtersSlice";
import { StoreWithSimilarityScore } from "modules/customer/insights/portfolioNew/store";
import { useAppDispatch, useAppSelector } from "store";
import { selectShowSimilarityScores } from "modules/customer/insights/portfolioNew/portfolioSlice";

const NUMBER_OF_VISIBLE_STORES_INCREMENT = 15;

const StyledCard = withStyles(theme => ({
    root: {
        // @ts-ignore
        backgroundColor: theme.palette.quaternary.dark
    }
}))(Card);

const CustomSelection: React.FC = () => {
    const dispatch = useAppDispatch();
    const theme = useTheme();
    const customSelectionStores = useAppSelector(selectCustomSelectionStores);
    const candidateComparator = useAppSelector(selectCandidateComparator);
    const showSimilarityScores = useAppSelector(selectShowSimilarityScores);
    const [numberOfVisibleStores, setNumberOfVisibleStores] = React.useState<number>(0);
    const storesLabel = `${customSelectionStores.length} ${customSelectionStores.length === 1 ? "store" : "stores"}`;

    const visibleStores = React.useMemo(() => {
        return customSelectionStores.slice(0, numberOfVisibleStores);
    }, [customSelectionStores, numberOfVisibleStores]);

    const handleSelect = (store: StoreWithSimilarityScore) => {
        dispatch(addStoreToCandidateComparator(store));
    };

    const handleDeselect = (store: StoreWithSimilarityScore) => {
        dispatch(removeStoreFromCandidateComparator(store));
    };

    const handleSelectAllClick = () => {
        customSelectionStores.forEach(store => {
            if (!candidateComparator?.getStores().some(s => s.id === store.id)) {
                dispatch(addStoreToCandidateComparator(store));
            }
        });
    };

    const handleDeselectAllClick = () => {
        customSelectionStores.forEach(store => {
            dispatch(removeStoreFromCandidateComparator(store));
        });
    };

    const handleLoadMore = React.useCallback(() => {
        setNumberOfVisibleStores(previousNumberOfVisibleStores => previousNumberOfVisibleStores + NUMBER_OF_VISIBLE_STORES_INCREMENT);
    }, [setNumberOfVisibleStores]);

    React.useEffect(() => {
        setNumberOfVisibleStores(NUMBER_OF_VISIBLE_STORES_INCREMENT);
    }, [customSelectionStores]);

    return (
        <InfiniteScroll
            initialLoad={false}
            loadMore={handleLoadMore}
            hasMore={numberOfVisibleStores <= customSelectionStores.length}
            useWindow={false}
        >
            <Box marginBottom={4}>
                <Box position="sticky" top={0} bgcolor={theme.palette.background.paper} zIndex={theme.zIndex.drawer}>
                    <Box display="flex" alignItems="center">
                        <Box>
                            <Search />
                        </Box>
                        <Box paddingLeft={2}>
                            {(showSimilarityScores) && (<DashAIComparatorsButton />)}
                        </Box>
                        
                    </Box>
                    
                    <Box display="flex" alignItems="center" paddingTop={2} paddingBottom={0.5}>
                        <Typography variant="body1" component="div">
                            &nbsp;&nbsp;{storesLabel}
                        </Typography>
                        &nbsp;&nbsp;
                        <Button
                            variant="text"
                            size="small"
                            color="default"
                            disableElevation
                            onClick={handleSelectAllClick}
                            data-cy="btn-select-all"
                        >
                            Select all
                        </Button>
                        &nbsp;&nbsp;
                        <Button
                            variant="text"
                            size="small"
                            color="default"
                            disableElevation
                            onClick={handleDeselectAllClick}
                            data-cy="btn-deselect-all"
                        >
                            Deselect all
                        </Button>
                        <Spacer />
                        <Filter />
                        &nbsp;&nbsp;
                        <Sort />
                    </Box>
                </Box>
                {visibleStores.length > 0 && (
                    <StyledCard elevation={0}>
                        <CardContent>
                            <Grid container spacing={2}>
                                {visibleStores.map(customSelectionStore =>
                                    <Grid key={customSelectionStore.id} item xs={4}>
                                        <StoreCard
                                            store={customSelectionStore}
                                            selected={candidateComparator?.getStores().some(store => store.id === customSelectionStore.id) ?? false}
                                            onSelect={handleSelect}
                                            onDeselect={handleDeselect}
                                            showSimilarityScore={showSimilarityScores}
                                        />
                                    </Grid>
                                )}
                            </Grid>
                        </CardContent>
                    </StyledCard>
                )}
            </Box>
        </InfiniteScroll>
    );
};

export default CustomSelection;
